import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Phone, Mail, MessageSquareText, CalendarDays, StickyNote,
  Loader2, Send, Clock, Bot, Mic,
} from "lucide-react";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const time = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const STATUS = { confirmado: "text-emerald-300", cancelado: "text-red-300", remarcado: "text-amber-300", pendente: "text-slate-300" };

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    const { data } = await api.get(`/clients/${id}`);
    setData(data);
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/clients/${id}/notes`, { text: note });
    setNote(""); toast.success("Observação salva.");
    load();
  };

  if (!data) return <div className="grid h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;
  const { client, appointments, messages } = data;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="client-profile-page">
      <button onClick={() => navigate("/crm")} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="profile-back-btn"><ArrowLeft className="h-4 w-4" /> Voltar ao CRM</button>

      <div className="glow-card rounded-2xl p-6" data-testid="profile-customer-header">
        <div className="flex flex-wrap items-center gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-cyan-400/15 text-2xl font-bold text-cyan-300">{initials(client.name)}</div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-extrabold text-white">{client.name}</h1>
            <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-400">
              {client.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {client.phone}</span>}
              {client.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {client.email}</span>}
            </div>
            <div className="mt-2 flex gap-1.5">{(client.tags || []).map((t) => <Badge key={t} className="border-cyan-400/30 bg-cyan-400/10 text-cyan-300">{t}</Badge>)}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="rounded-xl bg-slate-800/60 px-5 py-3"><p className="font-display text-2xl font-bold text-cyan-400">{appointments.length}</p><p className="text-xs text-slate-400">Agendamentos</p></div>
            <div className="rounded-xl bg-slate-800/60 px-5 py-3"><p className="font-display text-2xl font-bold text-blue-400">{messages.length}</p><p className="text-xs text-slate-400">Mensagens</p></div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="mensagens">
        <TabsList className="bg-slate-900/70">
          <TabsTrigger value="mensagens" data-testid="profile-messages-tab"><MessageSquareText className="mr-1.5 h-4 w-4" /> Mensagens</TabsTrigger>
          <TabsTrigger value="agendamentos" data-testid="profile-appointments-tab"><CalendarDays className="mr-1.5 h-4 w-4" /> Agendamentos</TabsTrigger>
          <TabsTrigger value="observacoes" data-testid="profile-notes-tab"><StickyNote className="mr-1.5 h-4 w-4" /> Observações</TabsTrigger>
        </TabsList>

        <TabsContent value="mensagens" className="mt-4">
          <div className="glow-card space-y-3 rounded-2xl p-5">
            {messages.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Sem histórico de mensagens.</p>}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm ${m.direction === "out" ? "bg-cyan-400/12 text-cyan-50 border border-cyan-400/25" : "bg-slate-800/80 text-slate-100"}`}>
                  {m.type === "voice" && <p className="mb-1 flex items-center gap-1 text-[11px] text-cyan-400"><Mic className="h-3 w-3" /> Voz (Whisper)</p>}
                  {m.ai_generated && <p className="mb-1 flex items-center gap-1 text-[11px] text-cyan-400"><Bot className="h-3 w-3" /> IA</p>}
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  <p className="mt-1 text-right text-[10px] opacity-60">{time(m.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="agendamentos" className="mt-4">
          <div className="glow-card space-y-3 rounded-2xl p-5">
            {appointments.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Nenhum agendamento.</p>}
            {appointments.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-xl border border-slate-800/60 bg-slate-900/40 p-4">
                <div><p className="font-medium text-slate-100">{a.title}</p><p className="flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" /> {time(a.scheduled_at)}</p></div>
                <span className={`text-sm font-medium capitalize ${STATUS[a.status] || "text-slate-300"}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="observacoes" className="mt-4">
          <div className="glow-card space-y-4 rounded-2xl p-5">
            <div className="flex gap-2">
              <Textarea data-testid="profile-add-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Adicionar observação interna..." className="border-slate-700 bg-slate-900/60" />
              <Button onClick={addNote} data-testid="profile-add-note-btn" className="h-auto bg-cyan-400 font-semibold text-slate-950 hover:bg-cyan-300"><Send className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-2">
              {(client.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="rounded-xl border border-slate-800/60 bg-slate-900/40 p-3">
                  <p className="text-sm text-slate-100">{n.text}</p>
                  <p className="mt-1 text-[11px] text-slate-500">{n.author} · {time(n.created_at)}</p>
                </div>
              ))}
              {(!client.notes || client.notes.length === 0) && <p className="py-4 text-center text-sm text-slate-500">Nenhuma observação ainda.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
